import { create } from 'zustand';
import { api } from '../services/api';

// Downstream dependency map: re-editing stage X makes these stages stale.
// stage7/stage9 (social) retired 2026-07-05 — social content moves to the Social Engine.
const DOWNSTREAM_MAP: Record<string, string[]> = {
  stage1: ['stage1b', 'stage2', 'stage3', 'stage4', 'stage6'],
  stage1b: ['stage6'],
  stage2: [],
  stage3: [],
  stage4: [],
  stage5: [],
  stage6: [],
  stage8: [],
};

interface StaleState {
  staleStages: string[];
  staleByWordId: Record<string, string[]>;
  loading: boolean;
  currentWordId: string | null;
  // Set when a stale-state PUT fails; the optimistic local change is rolled
  // back and this surfaces the failure so callers/UI can signal the operator.
  // Cleared on the next successful stale mutation. (M10 fix, 2026-07-05.)
  staleError: string | null;
  clearStaleError: () => void;
  fetchStale: (wordId: string | null) => Promise<void>;
  markDownstreamStale: (wordId: string, reEditedStage: string) => Promise<void>;
  clearStaleStage: (wordId: string, stageId: string) => Promise<void>;
  clearAllStale: (wordId: string) => Promise<void>;
  getDownstream: (stageId: string) => string[];
  isStale: (stageId: string) => boolean;
  hasAnyStale: () => boolean;
  wordHasStale: (wordId: string) => boolean;
}

export const useStaleStore = create<StaleState>()((set, get) => ({
  staleStages: [],
  staleByWordId: {},
  loading: false,
  currentWordId: null,
  staleError: null,

  clearStaleError: () => set({ staleError: null }),

  fetchStale: async (wordId: string | null) => {
    if (!wordId) {
      set({ staleStages: [], currentWordId: null });
      return;
    }
    set({ loading: true, currentWordId: wordId });
    try {
      const data = await api.get(`/api/words/${wordId}/stale`) as { staleStages?: string[] };
      const stages: string[] = data.staleStages || [];
      set((s) => ({
        staleStages: stages,
        staleByWordId: { ...s.staleByWordId, [wordId]: stages },
        loading: false,
      }));
    } catch {
      set({ staleStages: [], loading: false });
    }
  },

  markDownstreamStale: async (wordId: string, reEditedStage: string) => {
    const downstream = DOWNSTREAM_MAP[reEditedStage];
    if (!downstream || downstream.length === 0) return;

    // Snapshot for rollback if the persist fails.
    const prevStages = get().staleStages;
    const prevByWord = get().staleByWordId[wordId];

    const current = new Set(prevStages);
    for (const s of downstream) {
      current.add(s);
    }
    const merged = [...current];
    set((s) => ({
      staleStages: merged,
      staleByWordId: { ...s.staleByWordId, [wordId]: merged },
      staleError: null,
    }));
    try {
      await api.put(`/api/words/${wordId}/stale`, { staleStages: merged });
    } catch (err) {
      // Revert the optimistic change and surface the failure.
      set((s) => ({
        staleStages: get().currentWordId === wordId ? prevStages : s.staleStages,
        staleByWordId: { ...s.staleByWordId, [wordId]: prevByWord ?? [] },
        staleError: (err as Error).message || 'Failed to save stale state.',
      }));
    }
  },

  clearStaleStage: async (wordId: string, stageId: string) => {
    const prevStages = get().staleStages;
    const prevByWord = get().staleByWordId[wordId];

    const next = prevStages.filter((s) => s !== stageId);
    set((s) => ({
      staleStages: next,
      staleByWordId: { ...s.staleByWordId, [wordId]: next },
      staleError: null,
    }));
    try {
      await api.put(`/api/words/${wordId}/stale`, { staleStages: next });
    } catch (err) {
      set((s) => ({
        staleStages: get().currentWordId === wordId ? prevStages : s.staleStages,
        staleByWordId: { ...s.staleByWordId, [wordId]: prevByWord ?? [] },
        staleError: (err as Error).message || 'Failed to save stale state.',
      }));
    }
  },

  clearAllStale: async (wordId: string) => {
    const prevStages = get().staleStages;
    const prevByWord = get().staleByWordId[wordId];

    set((s) => ({
      staleStages: [],
      staleByWordId: { ...s.staleByWordId, [wordId]: [] },
      staleError: null,
    }));
    try {
      await api.put(`/api/words/${wordId}/stale`, { staleStages: [] });
    } catch (err) {
      set((s) => ({
        staleStages: get().currentWordId === wordId ? prevStages : s.staleStages,
        staleByWordId: { ...s.staleByWordId, [wordId]: prevByWord ?? [] },
        staleError: (err as Error).message || 'Failed to save stale state.',
      }));
    }
  },

  getDownstream: (stageId: string) => DOWNSTREAM_MAP[stageId] || [],
  isStale: (stageId: string) => get().staleStages.includes(stageId),
  hasAnyStale: () => get().staleStages.length > 0,
  wordHasStale: (wordId: string) => (get().staleByWordId[wordId]?.length || 0) > 0,
}));
